import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios, { readProfileUser } from '../utils/api';
import { redirectToPortalLogin } from '../utils/portalLogin';
import { resolvePublicPreviewUrl } from '../utils/previewAccess';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ComponentForm from '../components/ComponentForm';
import HtmlTemplateForm from '../components/HtmlTemplateForm';
import CodeEditor from '../components/CodeEditor';
import Preview from '../components/Preview';
import TemplateAreaManager from '../components/TemplateAreaManager';
import TemplatePreview from '../components/TemplatePreview';
import HtmlTemplateManager from '../components/HtmlTemplateManager';
import ContainerManager from '../components/ContainerManager';
import TemplatePlacementRules from '../components/TemplatePlacementRules';
import ComponentTester from './admin/ComponentTester';
import CentralDestination from '../components/CentralDestination';
import { formatBalanceUSD } from '../utils/adminHelpers';
import { 
  HtmlTemplate, 
  TemplateColumn, 
  TemplateArea, 
  TemplateComponent,
  ContentComponent
} from '../types/template';
import './Admin.css';

interface User {
  id: number;
  email: string;
  role: string;
  balance: number;
  currency?: 'USD';
  generation_limit: number;
  created_at: string;
  is_active: boolean;
  blocked_at: string | null;
  blocked_reason: string | null;
}

interface TemplateTypeOption {
  id: number;
  code: string;
  name: string;
}

type ApiErrorLike = {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
};

function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybe = error as ApiErrorLike;
  return maybe?.response?.data?.error || maybe?.response?.data?.message || fallback;
}

const COMPONENT_CATEGORY_LABELS: Record<string, string> = {
  hero: 'Hero',
  header: 'Хедер',
  navigation: 'Навигация',
  menu: 'Меню',
  about: 'About / О нас',
  services: 'Услуги',
  features: 'Преимущества',
  testimonials: 'Отзывы',
  pricing: 'Тарифы',
  gallery: 'Галерея',
  portfolio: 'Портфолио',
  blog: 'Блог',
  faq: 'FAQ',
  team: 'Команда',
  cta: 'CTA',
  contact: 'Контакты',
  footer: 'Футер',
  sidebar: 'Сайдбар',
  column: 'Колонки',
  other: 'Секции'
};

const CATEGORY_KEYS = Object.keys(COMPONENT_CATEGORY_LABELS);
const CATEGORY_ORDER = [
  'hero',
  'header',
  'navigation',
  'menu',
  'about',
  'services',
  'features',
  'testimonials',
  'pricing',
  'gallery',
  'portfolio',
  'blog',
  'faq',
  'team',
  'cta',
  'contact',
  'footer',
  'sidebar',
  'column',
  'other'
];

const PLACEMENT_SCOPE_LABELS: Record<string, string> = {
  primary: 'Основной блок',
  column: 'Колонки',
  universal: 'Универсальный',
  unknown: 'Не указано',
  undefined: 'Не указано'
};

const getCategoryLabel = (key: string) =>
  COMPONENT_CATEGORY_LABELS[key] ||
  key
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isHeaderComponent = (component: ContentComponent) => {
  const category = (component.component_name || '').split('-')[0]?.toLowerCase();
  return category === 'header';
};

const isNavigationComponent = (component: ContentComponent) => {
  const category = (component.component_name || '').split('-')[0]?.toLowerCase();
  const area = (component.template_area_type || '').toLowerCase();
  return category === 'navigation' || area === 'navigation' || area === 'menu';
};

const getMenuOrientation = (component: ContentComponent) => {
  const variant = (component.variant_code || '').toLowerCase();
  if (variant.includes('vertical') || variant.includes('sidebar') || variant.includes('burger')) {
    return 'vertical';
  }
  return 'horizontal';
};

interface Generation {
  id: number;
  generation_id: string;
  template_type: string;
  jurisdiction: string;
  site_type: string;
  language: string;
  status: string;
  created_at: string;
  user_email: string;
  archive_path?: string;
}



// Используем интерфейсы из types/template.ts

// Убираем дублирующиеся интерфейсы, так как они уже определены в types/template.ts

// Все интерфейсы уже определены в types/template.ts

const Admin: React.FC = () => {
  // Логи оставляем только для разработки, чтобы не засорять production и тесты.
  if (process.env.NODE_ENV === 'development') {
    console.log('Admin component is rendering');
  }
  const navigate = useNavigate();
  const location = useLocation();

  // Позволяет открывать конкретную вкладку через URL: /admin?tab=components
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!tab) return;

    const allowedTabs = new Set([
      'users',
      'generations',
      'components',
      'template-builder',
      'containers',
      'placement-rules',
      'settings',
      'component-tester',
    ]);

    if (allowedTabs.has(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);
  const [activeTab, setActiveTab] = useState('components');
  const [users, setUsers] = useState<User[]>([]);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [components, setComponents] = useState<ContentComponent[]>([]);
  const [htmlTemplates, setHtmlTemplates] = useState<HtmlTemplate[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [templateAreas, setTemplateAreas] = useState<TemplateArea[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [templateComponents, setTemplateComponents] = useState<TemplateComponent[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [templateTypes, setTemplateTypes] = useState<TemplateTypeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [balanceModalUser, setBalanceModalUser] = useState<User | null>(null);
  const [balanceDraft, setBalanceDraft] = useState<string>('');
  const [balanceReason, setBalanceReason] = useState<string>('');
  const [balanceSaving, setBalanceSaving] = useState(false);
  const [balanceSaveError, setBalanceSaveError] = useState<string>('');

  // Состояния для модалки блокировки пользователя
  const [blockModalUser, setBlockModalUser] = useState<User | null>(null);
  const [blockReason, setBlockReason] = useState<string>('');
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockError, setBlockError] = useState<string>('');

  // Состояния для форм
  const [showComponentForm, setShowComponentForm] = useState(false);
  const [showHtmlTemplateForm, setShowHtmlTemplateForm] = useState(false);
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplateAreaManager, setShowTemplateAreaManager] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  // Правила размещения компонентов в шаблонах (template_component_rules)
  // Выбранные элементы для редактирования
  const [selectedComponent, setSelectedComponent] = useState<ContentComponent | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<HtmlTemplate | null>(null);
  const [selectedTemplateAreas, setSelectedTemplateAreas] = useState<TemplateArea[]>([]);

  // Состояние для фильтрации компонентов
  const [showOnlyExistingComponents, setShowOnlyExistingComponents] = useState(false);
  const [componentSearchTerm, setComponentSearchTerm] = useState('');
  const [componentCategoryFilter, setComponentCategoryFilter] = useState('all');
  const [componentPlacementFilter, setComponentPlacementFilter] = useState<'all' | 'primary' | 'column' | 'universal' | 'unknown'>('all');
  const [componentPublishedFilter, setComponentPublishedFilter] = useState('all');
  const [headerMenuFilter, setHeaderMenuFilter] = useState('all');
  const [menuOrientationFilter, setMenuOrientationFilter] = useState('all');
  const [componentSortMode, setComponentSortMode] = useState<'category' | 'name'>('category');

  useEffect(() => {
    const init = async () => {
      await checkAuth();
      await fetchData();
    };
    init();
    // Нам важно не менять поведение (инициализация один раз при монтировании),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    console.log('checkAuth called');
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token, redirecting to login');
      redirectToPortalLogin();
      return;
    }

    // Проверяем роль пользователя через API
    try {
      console.log('Checking user role via API');
      const response = await axios.get('/api/auth/profile');
      const userRole = readProfileUser(response.data)?.role;
      console.log('User role:', userRole);
      if (userRole !== 'admin') {
        console.log('User is not admin, redirecting to home');
        navigate('/');
        return;
      }
      console.log('User is admin, auth successful');
    } catch (err) {
      console.error('Ошибка проверки роли пользователя:', err);
      redirectToPortalLogin();
      return;
    }
  };

  const fetchData = async () => {
    console.log('fetchData called');
    try {
      setLoading(true);
      console.log('Starting to fetch data...');
      await Promise.all([
        fetchGenerations(),
        fetchComponents(),
        fetchHtmlTemplates(),
        fetchTemplateComponents()
      ]);
      console.log('All data fetched successfully');
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
    }
  };

  const openBalanceModal = (targetUser: User) => {
    setBalanceSaveError('');
    setBalanceModalUser(targetUser);
    setBalanceDraft(String(targetUser.balance ?? 0));
    setBalanceReason('');
  };

  const closeBalanceModal = () => {
    setBalanceModalUser(null);
    setBalanceDraft('');
    setBalanceReason('');
    setBalanceSaveError('');
    setBalanceSaving(false);
  };

  const saveBalanceModal = async () => {
    if (!balanceModalUser) return;

    const parsed = Number(balanceDraft);
    if (!Number.isFinite(parsed)) {
      setBalanceSaveError('Введите корректное число');
      return;
    }
    if (parsed < 0) {
      setBalanceSaveError('Баланс не может быть отрицательным');
      return;
    }
    if (!balanceReason.trim()) {
      setBalanceSaveError('Укажите причину изменения баланса');
      return;
    }

    try {
      setBalanceSaving(true);
      setBalanceSaveError('');

      const response = await axios.put(`/api/admin/users/${balanceModalUser.id}/balance`, {
        balance: parsed,
        reason: balanceReason.trim()
      });
      const nextBalance = typeof response.data?.balance === 'number' ? response.data.balance : parsed;

      setUsers((prev) => prev.map((u) => (u.id === balanceModalUser.id ? { ...u, balance: nextBalance } : u)));
      setBalanceModalUser((prev) => (prev ? { ...prev, balance: nextBalance } : prev));
      closeBalanceModal();
    } catch (err: unknown) {
      setBalanceSaveError(getApiErrorMessage(err, 'Ошибка сохранения баланса'));
    } finally {
      setBalanceSaving(false);
    }
  };

  const fetchGenerations = async () => {
    try {
      const response = await axios.get('/api/admin/generations');
      // API возвращает { generations: [...], pagination: {...} }
      const generationsData = response.data.generations || response.data;
      setGenerations(Array.isArray(generationsData) ? generationsData : []);
      console.log('Загружено генераций:', generationsData?.length || 0);
    } catch (err) {
      console.error('Ошибка загрузки генераций:', err);
      setGenerations([]);
      setError('Не удалось загрузить генерации');
    }
  };

  const fetchComponents = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/components');
      setComponents(response.data);
    } catch (err) {
      console.error('Ошибка загрузки компонентов:', err);
    }
  }, []);

  const fetchHtmlTemplates = async () => {
    try {
      const response = await axios.get('/api/admin/html-templates');
      setHtmlTemplates(response.data);
    } catch (err) {
      console.error('Ошибка загрузки HTML-шаблонов:', err);
    }
  };


  const fetchTemplateComponents = async () => {
    try {
      const response = await axios.get('/api/admin/template-components');
      setTemplateComponents(response.data);
    } catch (err) {
      console.error('Ошибка загрузки компонентов шаблонов:', err);
    }
  };



  const handleComponentSave = useCallback(async (component: ContentComponent) => {
    try {
      if (component.id) {
        await axios.put(`/api/admin/components/${component.id}`, component);
      } else {
        await axios.post('/api/admin/components', component);
      }
      await fetchComponents();
      setShowComponentForm(false);
      setSelectedComponent(null);
    } catch (err) {
      console.error('Ошибка сохранения компонента:', err);
      setError('Не удалось сохранить компонент');
    }
  }, [fetchComponents]);

  const handleComponentEdit = useCallback((component: ContentComponent) => {
    console.log('=== Admin: handleComponentEdit called ===');
    console.log('Component to edit:', component);
    console.log('Component ID:', component.id);
    console.log('Component name:', component.component_name);
    console.log('File path:', component.file_path);
    console.log('Setting selectedComponent...');
    setSelectedComponent(component);
    console.log('Setting showComponentForm to true...');
    setShowComponentForm(true);
    console.log('handleComponentEdit completed');
  }, []);

  const handleComponentDelete = async (componentId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот компонент?')) {
      try {
        await axios.delete(`/api/admin/components/${componentId}`);
        await fetchComponents();
      } catch (err) {
        console.error('Ошибка удаления компонента:', err);
        setError('Не удалось удалить компонент');
      }
    }
  };

  // Обработчики для HTML-шаблонов
  const handleHtmlTemplateSave = async (templateData: HtmlTemplate & { columns?: TemplateColumn[]; areas?: TemplateArea[] }) => {
    try {
      let templateId: number;
      
      if (templateData.id) {
        // Обновляем существующий шаблон
        await axios.put(`/api/admin/html-templates/${templateData.id}`, templateData);
        templateId = templateData.id;
      } else {
        // Создаем новый шаблон
        const response = await axios.post('/api/admin/html-templates', templateData);
        templateId = response.data.id;
      }

      // Сохраняем колонки
      if (templateData.columns) {
        for (const column of templateData.columns) {
          column.template_id = templateId;
          if (column.id) {
            await axios.put(`/api/admin/template-columns/${column.id}`, column);
          } else {
            await axios.post('/api/admin/template-columns', column);
          }
        }
      }

      // Сохраняем области
      if (templateData.areas) {
        for (const area of templateData.areas) {
          area.template_id = templateId;
          if (area.id) {
            await axios.put(`/api/admin/template-areas/${area.id}`, area);
          } else {
            await axios.post('/api/admin/template-areas', area);
          }
        }
      }

      await fetchHtmlTemplates();
      setShowHtmlTemplateForm(false);
      setSelectedTemplate(null);
    } catch (err) {
      console.error('Ошибка сохранения HTML-шаблона:', err);
      setError('Не удалось сохранить HTML-шаблон');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleHtmlTemplateEdit = (template: HtmlTemplate) => {
    setSelectedTemplate(template);
    setShowHtmlTemplateForm(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleHtmlTemplateDelete = async (templateId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот HTML-шаблон?')) {
      try {
        await axios.delete(`/api/admin/html-templates/${templateId}`);
        await fetchHtmlTemplates();
      } catch (err) {
        console.error('Ошибка удаления HTML-шаблона:', err);
        setError('Не удалось удалить HTML-шаблон');
      }
    }
  };

  // Обработчики для областей шаблона
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTemplateAreaEdit = (template: HtmlTemplate) => {
    setSelectedTemplate(template);
    // Получаем области для выбранного шаблона
    const templateAreasForTemplate = templateAreas.filter((area: TemplateArea) => area.template_id === template.id);
    setSelectedTemplateAreas(templateAreasForTemplate);
    setShowTemplateAreaManager(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTemplatePreview = (template: HtmlTemplate) => {
    setSelectedTemplate(template);
    // Получаем области для выбранного шаблона
    const templateAreasForTemplate = templateAreas.filter((area: TemplateArea) => area.template_id === template.id);
    setSelectedTemplateAreas(templateAreasForTemplate);
    setShowTemplatePreview(true);
  };

  const resolveCategoryKey = (component: ContentComponent) => {
    const nameToken = component.component_name?.split('-')[0]?.toLowerCase();
    if (nameToken && CATEGORY_KEYS.includes(nameToken)) {
      return nameToken;
    }

    const templateToken = component.template_area_type?.toLowerCase();
    if (templateToken && CATEGORY_KEYS.includes(templateToken)) {
      return templateToken;
    }

    const variantToken = component.variant_code?.split('-')[0]?.toLowerCase();
    if (variantToken && CATEGORY_KEYS.includes(variantToken)) {
      return variantToken;
    }

    return 'other';
  };

  const resolvePlacementScope = (component: ContentComponent): 'primary' | 'column' | 'universal' | 'unknown' => {
    if (component.placement_scope === 'column' || component.placement_scope === 'universal') {
      return component.placement_scope;
    }
    if (component.placement_scope === 'primary' || !component.placement_scope) {
      return 'primary';
    }
    return 'unknown';
  };

  const isComponentPublished = (component: ContentComponent) => component.is_published !== false;

  const componentCategoryOptions = useMemo(() => {
    const unique = new Set<string>();
    components.forEach(component => unique.add(resolveCategoryKey(component)));

    const orderedKeys = Array.from(unique).sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) {
        return getCategoryLabel(a).localeCompare(getCategoryLabel(b));
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return orderedKeys.map(value => ({
      value,
      label: getCategoryLabel(value)
    }));
  }, [components]);

  const filteredComponents = useMemo(() => {
    const search = componentSearchTerm.trim().toLowerCase();
    return components
      .filter(component => {
        if (!showOnlyExistingComponents) {
          return true;
        }
        const path = component.file_path || '';
        return Boolean(path.trim()) && (path.includes('.html') || path.includes('.htm'));
      })
      .filter(component => {
        if (!search) return true;
        const haystack = `${component.component_name} ${component.variant_code}`.toLowerCase();
        return haystack.includes(search);
      })
      .filter(component => {
        if (componentCategoryFilter === 'all') return true;
        return resolveCategoryKey(component) === componentCategoryFilter;
      })
      .filter(component => {
        if (componentPlacementFilter === 'all') return true;
        const scope = resolvePlacementScope(component);
        return scope === componentPlacementFilter;
      })
      .filter(component => {
        if (componentPublishedFilter === 'all') return true;
        if (componentPublishedFilter === 'published') {
          return isComponentPublished(component);
        }
        if (componentPublishedFilter === 'draft') {
          return !isComponentPublished(component);
        }
        return true;
      })
      .filter(component => {
        if (headerMenuFilter === 'all') {
          return true;
        }
        if (!isHeaderComponent(component)) {
          return false;
        }
        if (headerMenuFilter === 'integrated') {
          return component.has_integrated_menu === true;
        }
        if (headerMenuFilter === 'requires-menu') {
          return component.requires_menu_component === true;
        }
        return true;
      })
      .filter(component => {
        if (menuOrientationFilter === 'all') {
          return true;
        }
        if (!isNavigationComponent(component)) {
          return false;
        }
        const orientation = getMenuOrientation(component);
        return orientation === menuOrientationFilter;
      })
      .sort((a, b) => {
        if (componentSortMode === 'category') {
          const catDiff = resolveCategoryKey(a).localeCompare(resolveCategoryKey(b));
          if (catDiff !== 0) return catDiff;
          return a.component_name.localeCompare(b.component_name);
        }
        return a.component_name.localeCompare(b.component_name);
      });
  }, [
    components,
    showOnlyExistingComponents,
    componentSearchTerm,
    componentCategoryFilter,
    componentPlacementFilter,
    componentPublishedFilter,
    headerMenuFilter,
    menuOrientationFilter,
    componentSortMode
  ]);

  const groupedComponents = useMemo(() => {
    if (componentSortMode === 'name') {
      return [
        {
          key: 'all',
          label: '',
          items: filteredComponents
        }
      ];
    }

    const groups = new Map<string, { label: string; items: ContentComponent[] }>();
    filteredComponents.forEach(component => {
      const key = resolveCategoryKey(component);
      if (!groups.has(key)) {
        groups.set(key, {
          label: COMPONENT_CATEGORY_LABELS[key] || COMPONENT_CATEGORY_LABELS.other,
          items: []
        });
      }
      groups.get(key)!.items.push(component);
    });
    return Array.from(groups.entries())
      .map(([key, value]) => ({ key, label: value.label, items: value.items }))
      .sort((a, b) => {
        const indexA = CATEGORY_ORDER.indexOf(a.key);
        const indexB = CATEGORY_ORDER.indexOf(b.key);
        if (indexA === -1 && indexB === -1) {
          return a.label.localeCompare(b.label);
        }
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
  }, [filteredComponents, componentSortMode]);

  // Обработчики для других функций

  const handleCodeEditorSave = async (component: ContentComponent) => {
    try {
      if (component.id) {
        await axios.put(`/api/admin/components/${component.id}`, component);
        await fetchComponents();
      }
      setShowCodeEditor(false);
      setSelectedComponent(null);
    } catch (err) {
      console.error('Ошибка сохранения кода компонента:', err);
      setError('Не удалось сохранить код компонента');
    }
  };

  // Обработчики для пользователей
  const handleUserDelete = async (userId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      try {
        await axios.delete(`/api/admin/users/${userId}`);
        await fetchUsers();
      } catch (err) {
        console.error('Ошибка удаления пользователя:', err);
        setError('Не удалось удалить пользователя');
      }
    }
  };

  // Открыть модалку блокировки
  const openBlockModal = (targetUser: User) => {
    setBlockError('');
    setBlockReason('');
    setBlockModalUser(targetUser);
  };

  // Закрыть модалку блокировки
  const closeBlockModal = () => {
    setBlockModalUser(null);
    setBlockReason('');
    setBlockError('');
    setBlockSaving(false);
  };

  // Заблокировать пользователя
  const handleBlockUser = async () => {
    if (!blockModalUser) return;

    try {
      setBlockSaving(true);
      setBlockError('');

      await axios.put(`/api/admin/users/${blockModalUser.id}/block`, {
        reason: blockReason.trim() || undefined
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.id === blockModalUser.id
            ? { ...u, is_active: false, blocked_at: new Date().toISOString(), blocked_reason: blockReason.trim() || null }
            : u
        )
      );
      closeBlockModal();
    } catch (err: unknown) {
      setBlockError(getApiErrorMessage(err, 'Ошибка блокировки пользователя'));
    } finally {
      setBlockSaving(false);
    }
  };

  // Разблокировать пользователя
  const handleUnblockUser = async (userId: number) => {
    if (!window.confirm('Вы уверены, что хотите разблокировать этого пользователя?')) return;

    try {
      await axios.put(`/api/admin/users/${userId}/unblock`);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, is_active: true, blocked_at: null, blocked_reason: null }
            : u
        )
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Ошибка разблокировки пользователя'));
    }
  };

  // Обработчики для генераций
  const handleGenerationDownload = async (generationId: string) => {
    try {
      const response = await axios.get(`/api/admin/generations/${generationId}/download`, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generation-${generationId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Ошибка скачивания генерации:', err);
      setError('Не удалось скачать генерацию');
    }
  };

  const handleGenerationDelete = async (generationId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту генерацию?')) {
      try {
        await axios.delete(`/api/admin/generations/${generationId}`);
        await fetchGenerations();
      } catch (err) {
        console.error('Ошибка удаления генерации:', err);
        setError('Не удалось удалить генерацию');
      }
    }
  };

  const handleGenerationPublicLink = async (generationId: string) => {
    try {
      const response = await axios.post(
        `/api/generation/preview-v2/${generationId}/public-link`
      );
      if (!response.data?.success) {
        const msg = response.data?.error || 'Не удалось получить публичную ссылку';
        alert(msg);
        return;
      }
      // Абсолютная ссылка (отдельный origin превью) остаётся как есть; относительная — от baseURL API.
      const publicUrl = resolvePublicPreviewUrl(response.data.publicUrl, axios.defaults.baseURL || window.location.origin);
      if (!publicUrl) {
        alert('Не удалось получить публичную ссылку');
        return;
      }
      try {
        await navigator.clipboard.writeText(publicUrl);
        alert(`Публичная ссылка скопирована (действует 24 часа):\n${publicUrl}`);
      } catch {
        window.prompt('Публичная ссылка (действует 24 часа):', publicUrl);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Не удалось получить публичную ссылку';
      console.error('Ошибка получения публичной ссылки:', msg);
      alert(msg);
    }
  };

  // Функция для закрытия формы компонента с логированием
  const handleComponentFormCancel = useCallback(() => {
    console.log('Admin: handleComponentFormCancel called - closing form');
    setShowComponentForm(false);
    setSelectedComponent(null);
  }, []);

  console.log('Admin component render - loading:', loading, 'error:', error);
  
  if (loading) {
    console.log('Showing loading state');
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-strong">Загрузка…</div>
          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
            Подготавливаем админ‑панель.
          </div>
        </div>
      </section>
    );
  }

  console.log('Rendering admin panel');
  if (activeTab === 'users') return <CentralDestination section="users" />;
  // Цены, прозрачность и публичные превью настраиваются в разделе Whitepage общей админки портала.
  if (activeTab === 'settings') return <CentralDestination section="whitepage" />;

  return (
    <div className="u2-grid" style={{ gap: 18 }} onClick={(e) => e.stopPropagation()}>
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h1 className="u2-title" style={{ fontSize: 22 }}>Управление Whitepage</h1>
              <p className="u2-subtitle">Компоненты, шаблоны и правила генерации</p>
            </div>
            <div className="u2-toolbarGroup">
              <Link className="u2-btn is-secondary" to="/dashboard">Дашборд</Link>
            </div>
          </div>

          {error && (
            <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent' }}>
              <div className="u2-cardInner">
                <div className="u2-row" style={{ alignItems: 'flex-start', gap: 12 }}>
                  <span className="u2-pill is-bad">ошибка</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="u2-muted" style={{ fontSize: 12 }}>{error}</div>
                    <div style={{ height: 10 }} />
                    <button onClick={() => setError('')} className="u2-btn is-secondary" type="button">
                      Закрыть
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="u2-subnav" role="navigation" aria-label="Разделы админ‑панели">
            <div className="u2-subnavInner">
              <button className="u2-tab" onClick={() => setActiveTab('users')} type="button">Общая админка ↗</button>
              <button className={`u2-tab ${activeTab === 'components' ? 'is-active' : ''}`} onClick={() => setActiveTab('components')} type="button">
                Компоненты
              </button>
              <button className={`u2-tab ${activeTab === 'template-builder' ? 'is-active' : ''}`} onClick={() => setActiveTab('template-builder')} type="button">
                Конструктор шаблонов
              </button>
              <button className={`u2-tab ${activeTab === 'containers' ? 'is-active' : ''}`} onClick={() => setActiveTab('containers')} type="button">
                Контейнеры
              </button>
              <button className={`u2-tab ${activeTab === 'placement-rules' ? 'is-active' : ''}`} onClick={() => setActiveTab('placement-rules')} type="button">
                Правила размещения
              </button>
              <button className="u2-tab" onClick={() => setActiveTab('settings')} type="button">Настройки ↗</button>
            </div>
          </div>
        </div>
      </section>

      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="admin-content">
        {/* Вкладка пользователей */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Управление пользователями</h2>
              <p>Просмотр и управление пользователями системы</p>
            </div>
            
            <div className="u2-tableWrap">
              <table className="u2-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Роль</th>
                    <th>Статус</th>
                    <th>Баланс, USD</th>
                    <th>Лимит генераций</th>
                    <th>Дата регистрации</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`user-role user-role--${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className={`user-status user-status--${user.is_active !== false ? 'active' : 'blocked'}`}>
                          {user.is_active !== false ? 'Активен' : 'Заблокирован'}
                        </span>
                        {user.blocked_at && (
                          <div className="user-blocked-info">
                            {new Date(user.blocked_at).toLocaleDateString()}
                            {user.blocked_reason && <span title={user.blocked_reason}>*</span>}
                          </div>
                        )}
                      </td>
                      <td>{formatBalanceUSD(user.balance)}</td>
                      <td>{user.generation_limit}</td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="u2-btn is-secondary" onClick={() => openBalanceModal(user)}>
                            Баланс
                          </button>
                          {user.is_active !== false ? (
                            <button type="button" className="u2-btn is-warning" onClick={() => openBlockModal(user)}>
                              Заблокировать
                            </button>
                          ) : (
                            <button type="button" className="u2-btn is-success" onClick={() => handleUnblockUser(user.id)}>
                              Разблокировать
                            </button>
                          )}
                          <button type="button" className="u2-btn is-danger" onClick={() => handleUserDelete(user.id)}>
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Вкладка генераций */}
        {activeTab === 'generations' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Управление генерациями</h2>
              <p>Просмотр и управление генерациями сайтов</p>
              <button
                onClick={() => fetchGenerations()}
                className="u2-btn is-secondary"
                title="Обновить список"
                type="button"
              >
                Обновить
              </button>
            </div>
            
            {generations.length === 0 ? (
              <div className="admin-empty-state">
                <div className="empty-icon" aria-hidden="true">—</div>
                <h3>Нет генераций</h3>
                <p>В системе пока нет генераций сайтов</p>
              </div>
            ) : (
              <div className="u2-tableWrap">
                <table className="u2-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Generation ID</th>
                      <th>Тип шаблона</th>
                      <th>Юрисдикция</th>
                      <th>Тип сайта</th>
                      <th>Язык</th>
                      <th>Статус</th>
                      <th>Пользователь</th>
                      <th>Дата создания</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generations.map(generation => (
                      <tr key={generation.id}>
                        <td>{generation.id}</td>
                        <td>{generation.generation_id}</td>
                        <td>{generation.template_type}</td>
                        <td>{generation.jurisdiction}</td>
                        <td>{generation.site_type}</td>
                        <td>{generation.language}</td>
                        <td>
                          <span className={`generation-status generation-status--${generation.status.toLowerCase()}`}>
                            {generation.status === 'completed' && 'Завершено'}
                            {generation.status === 'failed' && 'Ошибка'}
                            {generation.status === 'processing' && 'Обработка'}
                            {generation.status === 'pending' && 'Ожидание'}
                            {generation.status === 'error' && 'Ошибка'}
                            {!['completed', 'failed', 'processing', 'pending', 'error'].includes(generation.status) && generation.status}
                          </span>
                        </td>
                        <td>{generation.user_email}</td>
                        <td>{new Date(generation.created_at).toLocaleDateString()}</td>
                        <td>
                          {generation.archive_path && (
                            <button
                              onClick={() => handleGenerationDownload(generation.generation_id)}
                              className="u2-btn is-secondary"
                              title="Скачать архив"
                              type="button"
                            >
                              Скачать
                            </button>
                          )}
                          {generation.status === 'completed' && (
                            <button
                              onClick={() => handleGenerationPublicLink(generation.generation_id)}
                              className="u2-btn is-secondary"
                              title="Получить публичную ссылку (24 часа)"
                              type="button"
                            >
                              Публичная ссылка
                            </button>
                          )}
                          <button
                            onClick={() => handleGenerationDelete(generation.id)}
                            className="u2-btn is-danger"
                            title="Удалить"
                            type="button"
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Вкладка компонентов */}
        {activeTab === 'components' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2>Управление компонентами</h2>
                <p>Создание и редактирование компонентов для вайтпейджей</p>
              </div>
              <div className="header-actions">
                <button
                  onClick={() => setShowComponentForm(true)}
                  className="u2-btn is-primary"
                  type="button"
                >
                  + Создать компонент
                </button>
                <button
                  onClick={() => setShowOnlyExistingComponents(!showOnlyExistingComponents)}
                  className={`u2-btn ${showOnlyExistingComponents ? 'is-secondary' : 'is-ghost'}`}
                  title={showOnlyExistingComponents ? 'Показать все компоненты' : 'Скрыть компоненты без HTML'}
                  type="button"
                >
                  {showOnlyExistingComponents ? 'Показать все' : 'Скрыть без HTML'}
                </button>
              </div>
            </div>

            <div className="component-filters">
              <div className="filter-group">
                <label className="u2-label">Поиск</label>
                <input
                  type="text"
                  className="u2-input"
                  placeholder="Название или вариант..."
                  value={componentSearchTerm}
                  onChange={(e) => setComponentSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="u2-label">Категория</label>
                <select
                  className="u2-select"
                  value={componentCategoryFilter}
                  onChange={(e) => setComponentCategoryFilter(e.target.value)}
                >
                  <option value="all">Все</option>
                  {componentCategoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="u2-label">Размещение</label>
                <select
                  className="u2-select"
                  value={componentPlacementFilter}
                  onChange={(e) =>
                    setComponentPlacementFilter(e.target.value as 'all' | 'primary' | 'column' | 'universal' | 'unknown')
                  }
                >
                  <option value="all">Все</option>
                  {['primary', 'column', 'universal', 'unknown'].map(scope => (
                    <option key={scope} value={scope}>
                      {PLACEMENT_SCOPE_LABELS[scope as keyof typeof PLACEMENT_SCOPE_LABELS]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="u2-label">Публикация</label>
                <select
                  className="u2-select"
                  value={componentPublishedFilter}
                  onChange={(e) => setComponentPublishedFilter(e.target.value)}
                >
                  <option value="all">Все</option>
                  <option value="published">Только опубликованные</option>
                  <option value="draft">Только черновики</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="u2-label">Хедер</label>
                <select
                  className="u2-select"
                  value={headerMenuFilter}
                  onChange={(e) => setHeaderMenuFilter(e.target.value)}
                >
                  <option value="all">Все</option>
                  <option value="integrated">Только со встроенным меню</option>
                  <option value="requires-menu">Только требующие отдельное меню</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="u2-label">Тип меню</label>
                <select
                  className="u2-select"
                  value={menuOrientationFilter}
                  onChange={(e) => setMenuOrientationFilter(e.target.value)}
                >
                  <option value="all">Все</option>
                  <option value="horizontal">Горизонтальное</option>
                  <option value="vertical">Вертикальное (для колонок)</option>
                </select>
              </div>
            </div>

            <div className="component-sort-controls">
              <span className="u2-muted" style={{ fontSize: 12 }}>Сортировать по:</span>
              <div className="sort-toggle">
                <button
                  className={`u2-btn ${componentSortMode === 'category' ? 'is-primary' : 'is-ghost'}`}
                  onClick={() => setComponentSortMode('category')}
                  type="button"
                >
                  Категориям
                </button>
                <button
                  className={`u2-btn ${componentSortMode === 'name' ? 'is-primary' : 'is-ghost'}`}
                  onClick={() => setComponentSortMode('name')}
                  type="button"
                >
                  Названию
                </button>
              </div>
            </div>

            {filteredComponents.length === 0 ? (
              <div className="admin-empty-state">
                <div className="empty-icon">🧩</div>
                <h3>Нет компонентов</h3>
                <p>Измените фильтры или создайте новый компонент.</p>
              </div>
            ) : (
              <div className="component-table-wrapper">
                <table className="component-table">
                  <thead>
                    <tr>
                      <th>Компонент</th>
                      <th>Размещение</th>
                      <th>Меню и зависимости</th>
                      <th>Ограничения</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedComponents.map(({ key, label, items }) => (
                      <React.Fragment key={key}>
                        {componentSortMode === 'category' && (
                          <tr className="component-group-row">
                            <td colSpan={6}>
                              <span className="component-group-title">{label}</span>
                              <span className="component-group-count">{items.length} вариант(ов)</span>
                            </td>
                          </tr>
                        )}
                        {items.map(component => {
                          const categoryKey = resolveCategoryKey(component);
                          const placementScope = resolvePlacementScope(component);
                          const published = isComponentPublished(component);
                          return (
                          <tr key={component.id}>
                            <td>
                              <div className="component-name-cell">
                                <div>
                                  <div className="component-name">{component.component_name}</div>
                                  <div className="component-variant">{component.variant_code}</div>
                                  <div className="component-name-meta">
                                    <span className="category-chip">
                                      {COMPONENT_CATEGORY_LABELS[categoryKey] || COMPONENT_CATEGORY_LABELS.other}
                                    </span>
                                    {component.file_path && (
                                      <button
                                        type="button"
                                        className="component-file-link"
                                        onClick={() => {
                                          setSelectedComponent(component);
                                          setShowCodeEditor(true);
                                        }}
                                      >
                                        {component.file_path}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="component-placement">
                                <span className={`scope-chip scope-chip--${placementScope}`}>
                                  {PLACEMENT_SCOPE_LABELS[placementScope] || PLACEMENT_SCOPE_LABELS.undefined}
                                </span>
                                {component.template_area_type && (
                                  <span className="placement-subtitle">
                                    Область: {COMPONENT_CATEGORY_LABELS[categoryKey] || COMPONENT_CATEGORY_LABELS.other}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="component-tag-list">
                                {component.has_integrated_menu && (
                                  <span className="component-tag component-tag--success">Встроенное меню</span>
                                )}
                                {component.requires_menu_component && (
                                  <span className="component-tag component-tag--warning">Нужно меню рядом</span>
                                )}
                                {component.required_components?.length ? (
                                  <span className="component-tag component-tag--info">
                                    Требует: {component.required_components.length}
                                  </span>
                                ) : null}
                                {component.incompatible_components?.length ? (
                                  <span className="component-tag component-tag--danger">
                                    Несовместимо: {component.incompatible_components.length}
                                  </span>
                                ) : null}
                                {!component.has_integrated_menu &&
                                  !component.requires_menu_component &&
                                  !(component.required_components?.length) &&
                                  !(component.incompatible_components?.length) && (
                                    <span className="component-tag component-tag--muted">Нет ограничений</span>
                                  )}
                              </div>
                            </td>
                            <td>
                              <div className="component-stat-block">
                                <span>Позиция</span>
                                <strong>{component.position_min} – {component.position_max || '∞'}</strong>
                              </div>
                              <div className="component-stat-block">
                                <span>Макс. использований</span>
                                <strong>{component.max_usage || '—'}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="status-chip-group">
                                <span className={`status-chip status-chip--${component.is_active ? 'active' : 'inactive'}`}>
                                  {component.is_active ? 'Активен' : 'Выключен'}
                                </span>
                                <span className={`status-chip status-chip--${published ? 'published' : 'draft'}`}>
                                  {published ? 'Опубликован' : 'Черновик'}
                                </span>
                              </div>
                            </td>
                            <td className="action-buttons">
                              <button
                                onClick={() => handleComponentEdit(component)}
                                className="u2-btn is-secondary"
                                title="Редактировать"
                                type="button"
                              >
                                Редактировать
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedComponent(component);
                                  setShowCodeEditor(true);
                                }}
                                className="u2-btn is-secondary"
                                title="Редактировать код"
                                type="button"
                              >
                                Код
                              </button>
                              <button
                                onClick={() => handleComponentDelete(component.id!)}
                                className="u2-btn is-danger"
                                title="Удалить"
                                type="button"
                              >
                                Удалить
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {activeTab === 'placement-rules' && <TemplatePlacementRules templates={htmlTemplates} />}

        {/* Менеджер HTML шаблонов */}
        {activeTab === 'template-builder' && (
          <div className="admin-content" style={{ height: 'calc(100vh - 120px)' }}>
            <HtmlTemplateManager
              onSave={(template) => {
                console.log('Шаблон сохранен:', template);
                // Можно добавить уведомление об успешном сохранении
              }}
            />
          </div>
        )}

        {/* Вкладка контейнеров */}
        {activeTab === 'containers' && (
          <div className="admin-section">
            <ContainerManager
              onSave={(container) => {
                console.log('Контейнер сохранен:', container);
              }}
            />
          </div>
        )}

        {/* Вкладка Component Tester */}
        {activeTab === 'component-tester' && (
          <ComponentTester />
        )}
          </div>
        </div>
      </section>

      {/* Модалка изменения баланса */}
      {balanceModalUser && (
        <div className="u2-modal is-open" role="dialog" aria-modal="true" aria-label="Изменение баланса пользователя">
          <button className="u2-modalBackdrop" type="button" onClick={closeBalanceModal} aria-label="Закрыть" />
          <div className="u2-modalCard">
            <div className="u2-modalHeader">
              <div style={{ minWidth: 0 }}>
                <div className="u2-strong">Изменить баланс</div>
                <div className="u2-muted" style={{ fontSize: 12 }} title={balanceModalUser.email}>
                  {balanceModalUser.email}
                </div>
              </div>
              <button className="u2-iconBtn" type="button" onClick={closeBalanceModal} aria-label="Закрыть">
                <svg className="u2-ico lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="u2-modalBody">
              {balanceSaveError ? (
                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent', marginBottom: 12 }}>
                  <div className="u2-cardInner">
                    <span className="u2-pill is-bad">ошибка</span>
                    <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>{balanceSaveError}</div>
                  </div>
                </div>
              ) : null}

              <div className="u2-grid cols-2" style={{ alignItems: 'end' }}>
                <div className="u2-field">
                  <div className="u2-label">Текущий баланс, USD</div>
                  <input className="u2-input" value={formatBalanceUSD(balanceModalUser.balance)} readOnly />
                </div>
                <div className="u2-field">
                  <div className="u2-label">Новый баланс, USD</div>
                  <input
                    className="u2-input"
                    type="number"
                    min={0}
                    step={1}
                    value={balanceDraft}
                    onChange={(e) => setBalanceDraft(e.target.value)}
                  />
                </div>
              </div>

              <div className="u2-field" style={{ marginTop: 16 }}>
                <div className="u2-label">Причина</div>
                <textarea
                  className="u2-input"
                  rows={3}
                  value={balanceReason}
                  onChange={(e) => setBalanceReason(e.target.value)}
                />
              </div>

              <div className="u2-divider" />
              <div className="u2-stepNav is-right">
                <button className="u2-btn is-secondary" type="button" onClick={closeBalanceModal} disabled={balanceSaving}>
                  Отмена
                </button>
                <button className="u2-btn is-primary" type="button" onClick={saveBalanceModal} disabled={balanceSaving}>
                  {balanceSaving ? 'Сохранение…' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модалка блокировки пользователя */}
      {blockModalUser && (
        <div className="u2-modal is-open" role="dialog" aria-modal="true" aria-label="Блокировка пользователя">
          <button className="u2-modalBackdrop" type="button" onClick={closeBlockModal} aria-label="Закрыть" />
          <div className="u2-modalCard">
            <div className="u2-modalHeader">
              <div style={{ minWidth: 0 }}>
                <div className="u2-strong">Заблокировать пользователя</div>
                <div className="u2-muted" style={{ fontSize: 12 }} title={blockModalUser.email}>
                  {blockModalUser.email}
                </div>
              </div>
              <button className="u2-iconBtn" type="button" onClick={closeBlockModal} aria-label="Закрыть">
                <svg className="u2-ico lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="u2-modalBody">
              {blockError ? (
                <div className="u2-card is-soft" style={{ boxShadow: 'none', borderColor: 'transparent', marginBottom: 12 }}>
                  <div className="u2-cardInner">
                    <span className="u2-pill is-bad">ошибка</span>
                    <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>{blockError}</div>
                  </div>
                </div>
              ) : null}

              <div className="u2-field">
                <div className="u2-label">Причина блокировки (опционально)</div>
                <textarea
                  className="u2-textarea"
                  placeholder="Например: нарушение правил использования сервиса"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="u2-muted" style={{ fontSize: 12, marginTop: 8 }}>
                Пользователь не сможет войти в систему после блокировки. Вы можете разблокировать его в любой момент.
              </div>

              <div className="u2-divider" />
              <div className="u2-stepNav is-right">
                <button className="u2-btn is-secondary" type="button" onClick={closeBlockModal} disabled={blockSaving}>
                  Отмена
                </button>
                <button className="u2-btn is-danger" type="button" onClick={handleBlockUser} disabled={blockSaving}>
                  {blockSaving ? 'Блокировка…' : 'Заблокировать'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна */}
      {showComponentForm && (
        <div 
          className="component-form-wrapper" 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
        >
          <ComponentForm
            component={selectedComponent}
            templateTypes={templateTypes}
            allComponents={components}
            onSave={handleComponentSave}
            onCancel={handleComponentFormCancel}
          />
        </div>
      )}

      {showHtmlTemplateForm && (
        <div 
          className="component-form-wrapper" 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
        >
          <HtmlTemplateForm
            template={selectedTemplate}
            templateTypes={templateTypes}
            onSave={handleHtmlTemplateSave}
            onCancel={() => {
              setShowHtmlTemplateForm(false);
              setSelectedTemplate(null);
            }}
          />
        </div>
      )}


      {showCodeEditor && selectedComponent && (
        <div 
          className="component-form-wrapper" 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
        >
          <CodeEditor
            component={selectedComponent}
            onSave={handleCodeEditorSave}
            onCancel={() => {
              setShowCodeEditor(false);
              setSelectedComponent(null);
            }}
          />
        </div>
      )}

      {showPreview && (
        <div 
          className="component-form-wrapper" 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
        >
          <Preview
            onClose={() => setShowPreview(false)}
          />
        </div>
      )}

      {showTemplateAreaManager && selectedTemplate && (
        <div 
          className="component-form-wrapper" 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
        >
          <TemplateAreaManager
            templateId={selectedTemplate.id!}
            onAreasChange={(areas) => {
              setSelectedTemplateAreas(areas);
              setShowTemplateAreaManager(false);
            }}
            onCancel={() => {
              setShowTemplateAreaManager(false);
              setSelectedTemplate(null);
            }}
          />
        </div>
      )}

        {showTemplatePreview && selectedTemplate && selectedTemplateAreas && (
        <div 
          className="component-form-wrapper" 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
        >
          <TemplatePreview
            template={selectedTemplate}
            areas={selectedTemplateAreas}
            onClose={() => {
              setShowTemplatePreview(false);
              setSelectedTemplate(null);
              setSelectedTemplateAreas([]);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Admin;
